// Kinesia — libreta interactiva en 2D: páginas reales en alta resolución, espiral vectorial,
// paso de página animado, medidas, vista de canto (grosor), trasera y ampliar página.
// Usa el mismo marcado y botones que la sección kn-libreta3d (data-kn3d-*).

const SIZES = { A4: { w: 21, h: 29.7 }, A5: { w: 14.85, h: 21 } };
const THICK = 1.9; // cm, 150 hojas + tapas
const FLIP_MS = 720;

const fmt = (n) => String(n).replace('.', ',');

function sheetsFor(model, size) {
  const p = model === 'jugadas' ? (size === 'A4' ? 'jugadas4' : 'jugadas5') : 'pizarra';
  return [
    ['portada', null],
    ['ficha', 'plantilla'],
    ['cal1', 'cal2'],
    [p + '-a', p + '-b'],
    [p + '-a', p + '-b'],
    ['cupon', null],
  ];
}

// Espiral: cada anilla es una curva con degradado metálico (vector, nítida a cualquier tamaño)
function spiralSVG(kind, pw, ph, rings) {
  const pitch = ph / rings;
  const out = Math.max(10, pw * 0.05);
  const hole = Math.max(5, pw * 0.026);
  const id = 'g' + Math.random().toString(36).slice(2, 8);
  let paths = '';
  for (let i = 0; i < rings; i++) {
    const y = (i + 0.5) * pitch;
    if (kind === 'open') {
      // arco sobre el lomo, de un agujero al otro
      const d = `M ${-hole} ${y + pitch * 0.12} C ${-hole} ${y - pitch * 0.42}, ${hole} ${y - pitch * 0.42}, ${hole} ${y + pitch * 0.12}`;
      paths += `<path d="${d}" class="s-sh"/><path d="${d}" stroke="url(#${id})" class="s-w"/>`;
    } else {
      // anilla que sale del agujero, rodea el canto y vuelve
      const s = kind === 'back' ? -1 : 1;
      const d = `M ${s * hole} ${y - pitch * 0.1} C ${-s * out} ${y - pitch * 0.42}, ${-s * out} ${y + pitch * 0.4}, ${s * hole} ${y + pitch * 0.18}`;
      paths += `<path d="${d}" class="s-sh"/><path d="${d}" stroke="url(#${id})" class="s-w"/>`;
    }
  }
  const x0 = kind === 'open' ? -hole - 4 : -out - 4;
  const wv = kind === 'open' ? hole * 2 + 8 : out + hole + 8;
  const vx = kind === 'back' ? -hole - 4 : x0;
  return `<svg class="kn-lib__spiral kn-lib__spiral--${kind}" style="left:${vx}px" viewBox="${vx} 0 ${wv} ${ph}" width="${wv}" height="${ph}" aria-hidden="true">
    <defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#8d9096"/><stop offset=".35" stop-color="#2a2c30"/><stop offset=".6" stop-color="#0c0d0f"/><stop offset="1" stop-color="#55585e"/>
    </linearGradient></defs>${paths}</svg>`;
}

export function init(root) {
  const cfg = JSON.parse(root.dataset.config);
  const wrap = root.querySelector('[data-kn3d-canvas]');
  const caption = root.querySelector('[data-kn3d-caption]');
  const pageLabel = root.querySelector('[data-kn3d-page]');
  const names = cfg.pageNames || [];
  const state = { model: cfg.model, size: cfg.size, s: 0, view: 'front', measures: false, busy: false };

  wrap.innerHTML = `
    <div class="kn-lib" data-lib>
      <div class="kn-lib__view kn-lib__view--front" data-view="front">
        <div class="kn-lib__book" data-book>
          <div class="kn-lib__shadow"></div>
          <div class="kn-lib__depth"></div>
          <div class="kn-lib__page kn-lib__page--left" data-left><img alt="" decoding="async"><span class="kn-lib__holes"></span><span class="kn-lib__curve"></span></div>
          <div class="kn-lib__page kn-lib__page--right" data-right><img alt="" decoding="async"><span class="kn-lib__holes"></span><span class="kn-lib__curve"></span><span class="kn-lib__plastic"></span></div>
          <div class="kn-lib__spine" data-spine></div>
          <div class="kn-lib__measure kn-lib__measure--w" data-mw><span></span></div>
          <div class="kn-lib__measure kn-lib__measure--h" data-mh><span></span></div>
        </div>
      </div>
      <div class="kn-lib__view kn-lib__view--side" data-view="side">
        <div class="kn-lib__side" data-side>
          <div class="kn-lib__side-block"></div>
          <div class="kn-lib__measure kn-lib__measure--t"><span></span></div>
          <div class="kn-lib__measure kn-lib__measure--sh"><span></span></div>
        </div>
      </div>
      <div class="kn-lib__view kn-lib__view--back" data-view="back">
        <div class="kn-lib__backcover" data-back><div class="kn-lib__spine" data-back-spine></div></div>
      </div>
    </div>`;
  const lib = wrap.querySelector('[data-lib]');
  const book = lib.querySelector('[data-book]');
  const left = lib.querySelector('[data-left]');
  const right = lib.querySelector('[data-right]');
  const spine = lib.querySelector('[data-spine]');
  const side = lib.querySelector('[data-side]');
  const back = lib.querySelector('[data-back]');

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let geo = { pw: 300, ph: 424, rings: 46 };

  const src = (key) => (key ? cfg.tex[key] : '');
  const sheets = () => sheetsFor(state.model, state.size);
  const maxS = () => sheets().length - 1;
  const L = (s) => (s > 0 ? sheets()[s - 1][1] : null);
  const R = (s) => sheets()[s][0];

  // Precarga de las páginas del modelo/tamaño actual
  function preload() {
    const seen = new Set();
    for (const [a, b] of sheets()) for (const k of [a, b]) if (k && !seen.has(k)) { seen.add(k); const im = new Image(); im.decoding = 'async'; im.src = src(k); }
  }

  function setPage(el, key) {
    const img = el.querySelector('img');
    if (key) { img.src = src(key); img.style.visibility = ''; el.classList.remove('is-blank'); }
    else { img.removeAttribute('src'); img.style.visibility = 'hidden'; el.classList.add('is-blank'); }
  }

  function renderStatic() {
    setPage(right, R(state.s));
    setPage(left, L(state.s));
    book.classList.toggle('is-closed', state.s === 0);
    updateLabels();
  }

  function updateLabels() {
    const d = SIZES[state.size];
    caption.textContent = `${state.size} · ${fmt(d.w)} × ${fmt(d.h)} cm · 150 hojas (300 páginas) · ${fmt(THICK)} cm de grosor`;
    const v = state.view;
    pageLabel.textContent = v === 'side' ? `Grosor real: ${fmt(THICK)} cm` : v === 'back' ? 'Trasera · cartón negro' : (names[Math.min(state.s, names.length - 1)] || '');
    root.querySelectorAll('[data-kn3d-model]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.kn3dModel === state.model)));
    root.querySelectorAll('[data-kn3d-size]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.kn3dSize === state.size)));
    const front = v === 'front';
    root.querySelector('[data-kn3d-prev]').disabled = !front || state.s === 0;
    root.querySelector('[data-kn3d-next]').disabled = !front || state.s >= maxS();
    const m = root.querySelector('[data-kn3d-measure]');
    if (m) m.setAttribute('aria-pressed', String(state.measures && front));
    root.querySelector('[data-kn3d-side]')?.setAttribute('aria-pressed', String(v === 'side'));
    root.querySelector('[data-kn3d-flip]')?.setAttribute('aria-pressed', String(v === 'back'));
    lib.querySelector('[data-mw] span').textContent = `${fmt(d.w)} cm`;
    lib.querySelector('[data-mh] span').textContent = `${fmt(d.h)} cm`;
    lib.querySelector('.kn-lib__measure--t span').textContent = `${fmt(THICK)} cm`;
    lib.querySelector('.kn-lib__measure--sh span').textContent = `${fmt(d.h)} cm`;
    book.classList.toggle('show-measures', state.measures && front);
  }

  // Paso de página: una hoja con dos caras gira sobre la espiral
  function flip(dir) {
    if (state.busy || state.view !== 'front') return;
    const to = state.s + dir;
    if (to < 0 || to > maxS()) return;
    state.busy = true;
    const leaf = document.createElement('div');
    leaf.className = 'kn-lib__leaf ' + (dir > 0 ? 'is-fwd' : 'is-back');
    const frontKey = dir > 0 ? R(state.s) : L(state.s);
    const backKey = dir > 0 ? L(to) : R(to);
    leaf.innerHTML = `<div class="kn-lib__face kn-lib__face--a ${frontKey ? '' : 'is-blank'}">${frontKey ? `<img src="${src(frontKey)}" alt="">` : ''}<span class="kn-lib__holes"></span></div>
      <div class="kn-lib__face kn-lib__face--b ${backKey ? '' : 'is-blank'}">${backKey ? `<img src="${src(backKey)}" alt="">` : ''}<span class="kn-lib__holes"></span></div>`;
    book.appendChild(leaf);
    // Debajo ya se ve la página que queda al terminar
    if (dir > 0) setPage(right, R(to)); else setPage(left, L(to));
    if (dir > 0 && state.s === 0) book.classList.remove('is-closed');
    const done = () => {
      leaf.remove();
      state.s = to;
      renderStatic();
      state.busy = false;
    };
    if (reduce) { done(); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => leaf.classList.add('is-turning')));
    if (dir < 0 && to === 0) setTimeout(() => book.classList.add('is-closed'), FLIP_MS * 0.55);
    setTimeout(done, FLIP_MS + 30);
    state.measures = false;
    updateLabels();
  }

  function setView(v) {
    state.view = v;
    lib.dataset.current = v;
    updateLabels();
  }

  // Tamaños y espirales según el espacio libre (a la derecha del texto en escritorio, entre título y botones en móvil)
  function fit() {
    const r = wrap.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const head = root.querySelector('.kn3d__head').getBoundingClientRect();
    const panel = root.querySelector('.kn3d__panel').getBoundingClientRect();
    const landscape = r.width / r.height > 1.05;
    const l = landscape ? Math.min(r.width * 0.5, Math.max(0, Math.max(head.right, panel.right) - r.left + 32)) : 0;
    const t = landscape ? 28 : Math.max(0, head.bottom - r.top + 16);
    const b = landscape ? r.height - 28 : Math.min(r.height, panel.top - r.top - 16);
    const fw = r.width - l - (landscape ? 32 : 16);
    const fh = Math.max(160, b - t);
    Object.assign(lib.style, { left: l + 'px', top: t + 'px', width: (landscape ? fw : r.width - 32) + 'px', height: fh + 'px', marginLeft: landscape ? '0' : '16px' });
    // A4 de referencia: que quepa abierta (2 páginas) y con margen para la espiral y las medidas
    const a4w = Math.min((landscape ? fw : r.width - 32) / 2.25, (fh * 0.86) * (21 / 29.7));
    const d = SIZES[state.size];
    const pw = a4w * (d.w / 21);
    const ph = pw * (d.h / d.w);
    const rings = Math.round(d.h / 0.64);
    geo = { pw, ph, rings };
    lib.style.setProperty('--pw', pw + 'px');
    lib.style.setProperty('--ph', ph + 'px');
    lib.style.setProperty('--pitch', ph / rings + 'px');
    lib.style.setProperty('--tw', Math.max(10, (pw / d.w) * THICK) + 'px');
    spine.innerHTML = spiralSVG('closed', pw, ph, rings) + spiralSVG('open', pw, ph, rings);
    lib.querySelector('[data-back-spine]').innerHTML = spiralSVG('back', pw, ph, rings);
  }

  // Controles
  root.querySelector('[data-kn3d-prev]').addEventListener('click', () => flip(-1));
  root.querySelector('[data-kn3d-next]').addEventListener('click', () => flip(1));
  root.querySelector('[data-kn3d-flip]')?.addEventListener('click', () => setView(state.view === 'back' ? 'front' : 'back'));
  root.querySelector('[data-kn3d-side]')?.addEventListener('click', () => setView(state.view === 'side' ? 'front' : 'side'));
  root.querySelector('[data-kn3d-measure]')?.addEventListener('click', () => { if (state.view !== 'front') setView('front'); state.measures = !state.measures; updateLabels(); });
  root.querySelector('[data-kn3d-reset]')?.addEventListener('click', () => { state.s = 0; state.measures = false; setView('front'); renderStatic(); });
  root.querySelector('[data-kn3d-zoom]')?.addEventListener('click', openZoom);
  root.querySelectorAll('[data-kn3d-model]').forEach((b) => b.addEventListener('click', () => { state.model = b.dataset.kn3dModel; preload(); renderStatic(); }));
  root.querySelectorAll('[data-kn3d-size]').forEach((b) => b.addEventListener('click', () => { state.size = b.dataset.kn3dSize; preload(); fit(); renderStatic(); }));

  // Tocar o deslizar sobre la libreta
  let down = null;
  lib.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY }; });
  lib.addEventListener('pointerup', (e) => {
    if (!down) return;
    const dx = e.clientX - down.x, dy = e.clientY - down.y;
    down = null;
    if (state.view !== 'front') { setView('front'); return; }
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { flip(dx < 0 ? 1 : -1); return; }
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
      const br = book.getBoundingClientRect();
      flip(e.clientX > br.left + br.width / 2 || state.s === 0 ? 1 : -1);
    }
  });
  root.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') flip(1); if (e.key === 'ArrowLeft') flip(-1); });

  // Ampliar: las páginas visibles a pantalla completa
  let dlg;
  function openZoom() {
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.className = 'kn-lib__zoom';
      dlg.innerHTML = '<button type="button" class="kn-lib__zoom-close" aria-label="Cerrar">×</button><div class="kn-lib__zoom-pages"></div>';
      document.body.appendChild(dlg);
      dlg.querySelector('button').addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    }
    const keys = state.s === 0 ? [R(0)] : [L(state.s), R(state.s)].filter(Boolean);
    dlg.querySelector('.kn-lib__zoom-pages').innerHTML = keys.map((k) => `<img src="${src(k)}" alt="Página de la libreta">`).join('');
    dlg.showModal();
  }

  new ResizeObserver(fit).observe(wrap);
  preload();
  fit();
  setView('front');
  renderStatic();
}

document.querySelectorAll('[data-kn3d]').forEach((root) => {
  try { init(root); } catch (e) { root.classList.add('is-fallback'); console.error(e); }
});
